/**
 * Fubo Design System Colors
 * Based on Figma Design System
 * Last update: Friday, May 14, 2025 at 4:00PM ET
 */

export const colors = {
  // Orange (primary accent color)
  // Orange is the heart of our brand colors, it suggests the energy of live sports.
  // We use it sparingly to highlight the most important elements such as the logo,
  // promotional offers, and primary calls to action.
  orange: {
    140: '#FF8964', // Light solid
    100: '#FF3A03', // Medium solid
    90: '#E03100', // Dark solid
    lightGradient: {
      from: '#FF8964',
      to: '#FF3A03',
    },
    mediumGradient: {
      from: '#FF3A03',
      to: '#E3001B',
    },
  },
  yellow: {
    100: '#ffc107',
  },

  // Neutrals
  // Neutral colors form the foundation of most layouts, applying to backgrounds,
  // text, and supporting elements. A range of tones allows us to create depth,
  // clarity, and visual hierarchy without overwhelming the content.
  white: '#FFFFFF',
  slate: {
    190: '#FAFBFF', // Lightest solid
    160: '#D5D8E7', // Lighter solid
    100: '#7F859F', // Light solid
    60: '#2C2F3D', // Medium solid
    30: '#161822', // Dark solid
    10: '#0B0C12', // Darker solid

    lightGradient: {
      from: '#2C2F3D',
      to: '#161822',
    },
    mediumGradient: {
      from: '#161822',
      to: '#0B0C12',
    },
  },
  black: '#000000',

  // Midnight
  // Midnight tones offer subtle contrast and are ideal for background elements
  // like banners and plan cards. These shades are used across product and growth designs.
  midnight: {
    40: '#191B2C', // Light solid
    20: '#0D0F21', // Medium solid
    0: '#000215', // Dark solid
    lightGradient: {
      from: '#19273C',
      to: '#0D0F21',
    },
    mediumGradient: {
      from: '#191B2C',
      to: '#000215',
    },
  },

  // Other accent colors
  // Accent colors support usability by drawing attention to specific elements.
  // They help users identify promotions, status updates and important messaging.
  mint: {
    120: '#72FFB2', // Light
    80: '#42CA80', // Medium
  },
  blue: {
    120: '#62AAFF', // Light
    80: '#0368FF', // Medium
  },
  red: {
    120: '#FF304B', // Light
    80: '#D20019', // Medium
  },

  // Semantic colors for common use cases
  semantic: {
    success: '#42CA80', // mint-80
    info: '#0368FF', // blue-80
    warning: '#FF3A03', // orange-100
    error: '#D20019', // red-80
    text: {
      primary: '#FFFFFF',
      secondary: '#7F859F', // slate-100
      muted: '#AFAFAF',
      inverse: '#161822', // slate-30
    },
    background: {
      primary: '#161822', // slate-30
      secondary: '#2C2F3D', // slate-60
      tertiary: '#0B0C12', // slate-10
      inverse: '#FFFFFF',
    },
    border: {
      primary: '#2C2F3D', // slate-60
      secondary: '#D5D8E7', // slate-160
      accent: '#FF3A03', // orange-100
    },
  },

  // Gradients
  gradients: {
    orange: {
      light: 'linear-gradient(135deg, #FF8964 0%, #FF3A03 100%)',
      medium: 'linear-gradient(135deg, #FF3A03 40%, #E3001B 100%)',
    },
    slate: {
      light: 'linear-gradient(135deg, #2C2F3D 0%, #161822 100%)',
      medium: 'linear-gradient(135deg, #161822 0%, #0B0C12 100%)',
    },
    midnight: {
      light: 'linear-gradient(135deg, #19273C 0%, #0D0F21 100%)',
      medium: 'linear-gradient(135deg, #191B2C 1.25%, #000215 99.06%)',
    },
  },
}

// Helper functions for color manipulation
export const colorHelpers = {
  // Get color with opacity
  withOpacity: (color, opacity) => {
    // Convert hex to rgba
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  },

  // Get text color based on background
  getContrastText: (backgroundColor) => {
    const hex = backgroundColor.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 128 ? colors.black : colors.white
  },
}

export default colors
